import { HttpCallbackArgWithSdTracking } from "../../ShellCommon/logger/http-interceptor";
import { PerformanceEventRecorder } from "./performance-logger/performance-event-recorder";
import { PerformanceMeasureService } from "./performance-logger/performance-measure";
import { WindowCommunicationBridgeService } from "../../ShellCommon/eventModule/window-communication-bridge";
import { LoggingService } from "./loggingSvc";
import { MsalAuthenticationServiceImplementation } from "../../ServiceDesk/sdServices/msal/msal-authentication-service";

export interface SignInReauthorizationService {
  /**
   * Opens a window for reauthorization in the event that the user signed out unexpectedly while keeping the existing browser session intact.
   * @param forLoginVerification A boolean value (default false). When true, window will self-close if things look good (useful for validation of authentication).
   * @param error A string value, represents error code returned by msal library
   *
   * @return A promise which will resolve when the user has completed the login flow in an indeterministic amount of time. This promise could never be resolved.
   */
  openReauthorizationWindow(forLoginVerification?: boolean, error?: string): ng.IPromise<void>;

  /**
   * @returns true if reauthorization window is open other wise false.
   */
  isReauthorizationInProgress(): boolean;

  /**
   * cancels reauthorization and closes popup.
   */
  cancelReauthorization(): void;
}

export let reauthorizationInProgressLocalStorageKeyName = "reauth_in_progress";
export let reauthorizationStartedEventName = "reauth_started";
export let reauthorizationCompletedEventName = "reauth_completed";

const reauthErrorString = "AADSTS50058";
export const reauthVerifyLoginQueryString = "verifyLogin";

// This service is being invoked from the main window that needs reauth, but not from the newly spawned window actually responsible for reauth.
export class SignInReauthorizationServiceImpl implements SignInReauthorizationService {
  public reauthorizationCompletedDeferred: ng.IDeferred<void>;
  public hasRecentReauthorization = false;
  public recentReauthorizationWindowSeconds = 60 * 2; // The window is 2 minutes long
  private interactiveRenewalEvent: PerformanceEventRecorder;
  private lastOpenedWindow: Window;
  private recentReauthorizationResetPromise: ng.IPromise<void>;

  // @ngInject
  constructor(
    private $window: Window,
    private loggingService: LoggingService,
    private $q: ng.IQService,
    private $timeout: ng.ITimeoutService,
    private windowCommunicationBridgeService: WindowCommunicationBridgeService,
    private $rootScope: ng.IRootScopeService,
    private performanceMeasure: PerformanceMeasureService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation
  ) {
    this.interactiveRenewalEvent = this.performanceMeasure.getOrCreateEventRecorder("InteractiveRenewal", {
      description: "Msal token renewal interactive login"
    });
  }

  /**
   * @returns true if reauthorization window is open other wise false.
   */
  public isReauthorizationInProgress(): boolean {
    return this.hasRecentReauthorization || (this.lastOpenedWindow && !this.lastOpenedWindow.closed);
  }

  /**
   * cancels reauthorization and closes popup.
   */
  public cancelReauthorization(): void {
    // resetting to make sure its correct redirectUri is set on cancellation of ReauthPopup.
    //this.msalAuthenticationService.config.redirectUri = window.location.href;
    if (this.lastOpenedWindow && !this.lastOpenedWindow.closed) {
      this.lastOpenedWindow.close();
      this.cancelRecentReauthorizationTimer();
    }
  }

  /**
   * Opens a window for reauthorization in the event that the user signed out unexpectedly while keeping the existing browser session intact.
   * @param forLoginVerification A boolean value (default false). When true, window will self-close if things look good (useful for validation of authentication).
   * @param error A string value, represents error code returned by msal library
   *
   * @return A promise which will resolve when the user has completed the login flow in an indeterministic amount of time. This promise could never be resolved.
   */
  public openReauthorizationWindow(forLoginVerification?: boolean, error?: string): ng.IPromise<void> {
    // If the reauth window was recently opened, don't open a new window since the user could be typing
    if (!this.hasRecentReauthorization || (this.lastOpenedWindow && this.lastOpenedWindow.closed)) {
      this.loggingService.logInfo("No recent reauthorization in process, open/reopening a window");
      this.cancelRecentReauthorizationTimer();
      this.hasRecentReauthorization = true;
      this.recentReauthorizationResetPromise = this.$timeout(() => {
        this.hasRecentReauthorization = false;
      }, this.recentReauthorizationWindowSeconds * 1000);
      let target = `#/reauth${forLoginVerification ? "?" + reauthVerifyLoginQueryString : ""}`;
      target = target + (error ? (target.includes("?") ? "&" : "?") + "error=" + error : "");
      this.lastOpenedWindow = this.$window.open(
        target,
        "reauth",
        "height=768,width=1024,location=no,resizable=yes,scrollbars=yes"
      );

      if (this.lastOpenedWindow.focus) {
        this.lastOpenedWindow.focus();
      }
    } else {
      this.loggingService.logWarning(
        "Did not reopen the login window as a reauth was started within " +
          this.recentReauthorizationWindowSeconds +
          " seconds"
      );
    }

    // If we have already started reauthorization the client use the same promise
    if (!this.reauthorizationCompletedDeferred) {
      this.interactiveRenewalEvent.start();
      this.loggingService.logInfo("No active promise for reauthorization, creating a new one.");
      this.windowCommunicationBridgeService.publish(reauthorizationStartedEventName, {});
      this.reauthorizationCompletedDeferred = this.$q.defer<void>();
      this.windowCommunicationBridgeService.subscribe(
        reauthorizationCompletedEventName,
        () => {
          this.lastOpenedWindow = undefined;
          this.interactiveRenewalEvent.success();
          this.hasRecentReauthorization = false;
          this.reauthorizationCompletedDeferred.resolve();
        },
        this.$rootScope
      );
    }

    // When the reflow completes, make sure to reset the recentReauthorizationResetPromise
    if (this.recentReauthorizationResetPromise) {
      this.reauthorizationCompletedDeferred.promise.then(() => {
        this.cancelRecentReauthorizationTimer();
      });
    }

    return this.reauthorizationCompletedDeferred.promise;
  }

  private cancelRecentReauthorizationTimer(): void {
    this.hasRecentReauthorization = false;
    if (this.recentReauthorizationResetPromise) {
      this.$timeout.cancel(this.recentReauthorizationResetPromise);
    }
  }
}

export class SignInReauthorizationRun {
  constructor($rootScope: ng.IRootScopeService, signInReauthorizationService: SignInReauthorizationService) {
    let isReauthorizationError = (result: HttpCallbackArgWithSdTracking | any) => {
      return (
        (typeof result.data === "string" && result.data.includes(reauthErrorString)) ||
        (typeof result === "string" && (<any>result).includes(reauthErrorString))
      );
    };

    $rootScope.$on("msal:errorResponse", (event: any, rejection: HttpCallbackArgWithSdTracking) => {
      if (isReauthorizationError(rejection)) {
        signInReauthorizationService.openReauthorizationWindow();
      }
    });
  }
}
